import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import * as PAGE from "../../Constants/Page.js";

const MyTextInput = ({ label, ...props }) => {
    const [field, meta] = useField(props);

    return (
        <div className="form-group">
            <label htmlFor={props.id || props.name}>{label}</label>
            <input className="text-input form-control" {...field} {...props} />
            {meta.touched && meta.error ? (
                <div className="error">{meta.error}</div>
            ) : null}
        </div>
    );
};

const MyTextarea = ({ label, ...props }) => {
    const [field, meta] = useField(props);

    return (
        <div className="form-group">
            <label htmlFor={props.id || props.name}>{label}</label>
            <textarea className="text-input form-control" {...field} {...props} />
            {meta.touched && meta.error ? (
                <div className="error">{meta.error}</div>
            ) : null}
        </div>
    );
};

export default function CheckoutForm(props) {
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [disabled, setDisabled] = useState(true);

    const stripe = useStripe();
    const elements = useElements();

    const phoneRegExp = /^((\+[1-9]{1,4}[ \-]*)|(\([0-9]{2,3}\)[ \-]*)|([0-9]{2,4})[ \-]*)*?[0-9]{3,4}?[ \-]*[0-9]{3,4}?$/;

    const cardStyle = {
        style: {
            base: {
                color: "#32325d",
                fontFamily: "Arial, sans-serif",
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: "#32325d"
                }
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a"
            }
        },
        hidePostalCode: true
    };

    const handleChange = async (event) => {
        setDisabled(event.empty);
        setError(event.error ? event.error.message : "");
    };

    const getIsAvailable = async () => {
        const url =
            "api/availability?propertyKey=" +
            props.reservation.propertyKey +
            "&from=" +
            props.reservation.checkIn +
            "&nights=" +
            props.reservation.nights;

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw Error(response.statusText);
            }

            const available = await response.json();

            let returnValue = true;

            props.reservation.selectedRooms.forEach(function (room) {
                if (!available.includes(room.roomTypeId)) {
                    returnValue = false;
                }
            });

            return returnValue;
        } catch (error) {
            console.log(error);
            return false;
        }
    };

    const cancelBooking = async (bookingId) => {
        if (!bookingId) return;

        try {
            await fetch("api/checkout/cancel-booking", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ bookingId })
            });
        } catch (error) {
            console.log("Cancel booking failed:", error);
        }
    };

    const createBooking = async (values) => {
        let utm = {};

        try {
            utm = JSON.parse(localStorage.getItem("bookingUtm") || "{}");
        } catch {
            utm = {};
        }

        const reservation = {
            ...props.reservation,
            ...values,
            source: utm.source || "",
            medium: utm.medium || "",
            campaign: utm.campaign || "",
            referrer: utm.referrer || "",
            landingPage: utm.landingPage || ""
        };

        const response = await fetch("api/checkout/create-booking", {
            method: "POST",
            headers: {
                "Content-Type": "application/json; charset=utf-8"
            },
            body: JSON.stringify(reservation)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || "Booking could not be created.");
        }

        return data;
    };

    const finaliseBooking = async ({ bookingId, intentType, intentId }) => {
        const response = await fetch("api/checkout/finalise", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                bookingId,
                intentType,
                intentId,
                propertyKey: props.reservation.propertyKey,
                paymentKey: props.reservation.paymentKey
            })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(
                data.message ||
                "We could not confirm the booking. Please contact us before trying again."
            );
        }

        return data;
    };

    const handleCardSubmit = async (values) => {
        setProcessing(true);
        setError(null);

        if (!stripe || !elements) {
            setError("Payment form is still loading. Please try again.");
            setProcessing(false);
            return;
        }

        const isAvailable = await getIsAvailable();

        if (!isAvailable) {
            setProcessing(false);
            props.setPageState(
                PAGE.PAGE.FAIL,
                "In the time passed since selecting your reservation, our room availability has changed. Please try to make your reservation again."
            );
            return;
        }

        let bookingId = 0;

        try {
            const bookingResponse = await createBooking(values);

            bookingId = bookingResponse.bookingId;

            const card = elements.getElement(CardElement);

            if (bookingResponse.intentType === "setup") {
                const payload = await stripe.confirmCardSetup(
                    bookingResponse.clientSecret,
                    {
                        payment_method: {
                            card,
                            billing_details: {
                                name: values.firstName + " " + values.lastName,
                                email: values.email,
                                phone: values.telephone
                            }
                        }
                    }
                );

                if (payload.error) {
                    await cancelBooking(bookingId);
                    setError(
                        "Card verification failed. Your booking has not been confirmed."
                    );
                    setProcessing(false);
                    return;
                }

                await finaliseBooking({
                    bookingId,
                    intentType: "setup",
                    intentId: payload.setupIntent.id
                });
            } else {
                const payload = await stripe.confirmCardPayment(
                    bookingResponse.clientSecret,
                    {
                        receipt_email: values.email,
                        payment_method: {
                            card
                        }
                    }
                );

                if (payload.error) {
                    await cancelBooking(bookingId);
                    setError(
                        "Payment failed. Your booking has not been confirmed and your card has not been charged."
                    );
                    setProcessing(false);
                    return;
                }

                await finaliseBooking({
                    bookingId,
                    intentType: "payment",
                    intentId: payload.paymentIntent.id
                });
            }

            setSucceeded(true);
            setProcessing(false);
            props.setPageState(PAGE.PAGE.SUCCESSFUL, bookingId);
        } catch (error) {
            console.log("Checkout error:", error);

            setProcessing(false);

            if (bookingId > 0) {
                setError(
                    error.message ||
                    "Your booking/payment status could not be confirmed. Please contact us before trying again."
                );
            } else {
                setError(
                    error.message ||
                    "We were unable to complete your reservation. No payment has been taken."
                );
            }
        }
    };

    return (
        <>
            <Formik
                initialValues={{
                    titleId: "-1",
                    firstName: props.reservation.firstName,
                    lastName: props.reservation.lastName,
                    arrivalTime: "2000-01-01T14:00:00",
                    email: props.reservation.email,
                    telephone: props.reservation.telephone,
                    postcode: props.reservation.postcode || "",
                    guestInstructions: props.reservation.guestInstructions
                }}
                validationSchema={Yup.object().shape({
                    firstName: Yup.string().required("First name is required"),
                    lastName: Yup.string().required("Last name is required"),
                    email: Yup.string()
                        .email("Invalid email address format")
                        .required("Email is required"),
                    telephone: Yup.string()
                        .matches(phoneRegExp, "Phone number is not valid")
                        .required("Phone Number is required"),
                    postcode: Yup.string().required("Postcode is required")
                })}
                onSubmit={async (values, { setSubmitting }) => {
                    await handleCardSubmit(values);
                    setSubmitting(false);
                }}
            >
                {() => (
                    <Form>
                        <div className="form-row">
                            <div className="col-md-6">
                                <MyTextInput label="First Name" name="firstName" />
                            </div>
                            <div className="col-md-6">
                                <MyTextInput label="Last Name" name="lastName" />
                            </div>
                        </div>

                        <MyTextInput label="Email" name="email" />

                        <div className="form-row">
                            <div className="col-md-6">
                                <MyTextInput label="Mobile Phone" name="telephone" />
                            </div>
                            <div className="col-md-6">
                                <MyTextInput label="Postcode" name="postcode" />
                            </div>
                        </div>

                        <MyTextarea label="Instructions" name="guestInstructions" />

                        <CardElement
                            id="card-element"
                            options={cardStyle}
                            onChange={handleChange}
                        />

                        <button
                            className="payment-button"
                            disabled={processing || disabled || succeeded}
                            id="submit"
                            type="submit"
                        >
                            <span id="button-text">
                                {processing ? (
                                    <div className="spinner" id="spinner"></div>
                                ) : (
                                    "Reserve"
                                )}
                            </span>
                        </button>

                        {error && (
                            <div className="card-error" role="alert">
                                {error}
                            </div>
                        )}

                        <p
                            className={
                                succeeded
                                    ? "result-message"
                                    : "result-message hidden"
                            }
                        >
                            Card details accepted, please wait while we confirm your
                            reservation.
                        </p>
                    </Form>
                )}
            </Formik>
        </>
    );
}